import { CollectibleType } from '../collectibles/CollectibleType';

export interface JunctionConfig {
  /**
   * Straight segments required between two junctions (prevents loops and overlap). With the high
   * junction weights (junctions.ts: JUNCTION_WEIGHT_SCALE) it sets the rhythm: ≈ 4–5 junctions per km.
   */
  readonly minSegmentsBetween: number;
  /** Segments pre-built on every exit of an undecided T-junction/crossing. */
  readonly branchPreviewSegments: number;
  /**
   * Left/right input is a turn decision only on the junction square itself (user 2026-10-03: "ich muss wirklich rechts
   * drücken, wenn ich mich in der Abbiegung befinde. Das muss sehr exakt sein") – from `decisionWindow` meters (the
   * square) plus the distance covered in `decisionLatencySeconds` (touch latency) before the pivot. Before it, a press
   * is a plain lane change, never a turn kept for later. The latency share grows with the speed: in the 2.5 × turbo a
   * fixed distance left almost no time to hit the square.
   */
  readonly decisionWindow: number;
  readonly decisionLatencySeconds: number;
  /**
   * The junction ahead is announced this many meters before the pivot (TurnController.hint): the camera leans into a
   * decided turn and no drink break starts on the approach – wider than the decision window.
   */
  readonly approachWindow: number;
  /** A turn may still be executed this many meters after the pivot (outer lane needs ≈ lane width). */
  readonly lateTurnGrace: number;
  /** The track never heads further than this from the start direction – it can never run back into itself. */
  readonly maxHeadingDegrees: number;
  /**
   * Junctions and curves (SEG_15/16) may lead the track sideways (±90°) only this far (meters along
   * the start direction) beyond the last stretch that ran sideways the opposite way – the two
   * stretches pass each other with their side ground and backdrop (±30 m) clear of each other.
   */
  readonly minReversalSeparation: number;
  /**
   * Roadside bays of the module right behind a junction start at least this far along it: the
   * junction's corner – its approach fence (0.9 m), the closed-end building (5.7 m), the bays of
   * the neighbouring exits (8.3 m) – reaches into the first meters of every exit module.
   */
  readonly roadsideClearance: number;
}

/** Rules for segment terrain (ramps, platforms, pits, ceilings) – see level/terrain. */
export interface TerrainConfig {
  /**
   * Longest pit a single jump must clear = start speed × jump time − this margin (meters).
   * 8 m/s × 0.75 s − 1 m = 5 m. Longer pits need platforms in between.
   */
  readonly jumpGapSafety: number;
  /** Steepest walkable ramp (rise per meter along the track). */
  readonly maxRampSlope: number;
  /** Stretch after CONNECTOR_IN and before CONNECTOR_OUT that must be plain ground (segments connect at ground level). */
  readonly connectorClearance: number;
  /** Obstacle spawn points keep at least this distance from pits and terrain edges (meters). */
  readonly spawnClearance: number;
  /** After a fall with an extra life, the runner is put down on safe ground this far past the pit (meters). */
  readonly respawnMargin: number;
  /** How far ahead safe ground is searched after a fall (meters). */
  readonly respawnSearchDistance: number;
  /** Sampling step of that search and of the static lane analysis (meters). */
  readonly sampleStep: number;
  /** Terrain features / obstacle rows closer than this along the track form one decision row (meters). */
  readonly rowMergeDistance: number;
  /**
   * Gaps between two raised surfaces of the same height (containers in a row) that a runner on top
   * crosses without a jump: footprint depth + start speed × time to drop one step − this margin
   * (meters). 0.6 m + 8 m/s × 0.17 s − 0.4 m ≈ 1.5 m.
   */
  readonly runAcrossSafety: number;
  /**
   * Fairness of steps that need a double jump (container roofs), checked by the terrain analysis
   * at start and top speed with the module's own flat run-up (physics/JumpReach.ts).
   */
  readonly doubleJumpStep: {
    /** Input tolerance for the second jump at a good take-off point (seconds). */
    readonly minTriggerWindow: number;
    /** Span of take-off moments that still leave that tolerance (seconds). */
    readonly minTakeOffWindow: number;
  };
}

/**
 * Obstacles with a walkable top that only a double jump gets onto (the container OBS_CONTAINER_A, roof
 * 2.69 m – user 2026-10-02). The populator places one only where its lane offers a free run-up and a free
 * drop zone, hands its share of its type over to the spots where it fits, and lays a bottle line onto its roof
 * (level/spawning/roofLines.ts). Run-up and drop zone are no tuning values: they follow from the jump and fall
 * model, terrain.doubleJumpStep and the reaction time (level/terrain/roofRunUp.ts) – 13.7 m of plain road
 * before the face, 15.0 m free lane behind the roof.
 */
export interface RoofRouteConfig {
  /**
   * Probability that a placed container carries a bottle line on its roof. High: the roof is the bonus route
   * and most containers show it; the rest stay plain (their roof is walkable all the same).
   */
  readonly bottleChance: number;
  /** Spacing of the roof bottles along the track (m) – the same as on the designed bottle lines. */
  readonly bottleSpacing: number;
  /** The roof bottles keep this far from the front and the rear edge of the roof (m): one at each end of a 6 m roof. */
  readonly edgeMargin: number;
  /**
   * The line starts with the arc of the double jump up from the run-up (the ideal take-off at the speed the
   * module is met, like the container row of SEG_CONTAINERS_40M) – where that take-off lies inside the module.
   * Otherwise, and with false, the bottles lie on the roof only.
   */
  readonly arc: boolean;
}

/**
 * Beer crates = turbo (user 2026-10-03): placed only where they are hard to reach – the last bottle on a container
 * roof (double jump) or the highest bottle of a designed line from `minHeight` up (spawn point height above the
 * module floor; the jump arcs over obstacles stay lower). At most one crate per segment.
 */
export interface TurboCrateConfig {
  /** Probability that a roof line ends with a crate instead of its last bottle. */
  readonly roofChance: number;
  /** Probability that a high designed line has its highest bottle replaced by a crate. */
  readonly highLineChance: number;
  /** A designed line counts as high from this spawn point height (m above the module floor). */
  readonly minHeight: number;
  /** No crates before this run distance (m) – the turbo is a reward, not a start help. */
  readonly fromDistance: number;
}

/** Scheduled set pieces (golden ramp, dozer – SetPieceSchedule) among each other. */
export interface SetPiecesConfig {
  /**
   * Meters of ordinary track between the end of one set piece and the start of the next. Where two schedules
   * meet, the set piece with the lower priority waits (LevelGenerator) – the other keeps its mark.
   */
  readonly minGap: number;
}

export interface LevelConfig {
  /** Segments kept alive ahead of the player's current segment. */
  readonly segmentsAhead: number;
  /** Segments kept alive behind the player before they get recycled. */
  readonly segmentsBehind: number;
  /** Number of obstacle-free segments at the start of a run. */
  readonly safeStartSegments: number;
  /** Segment id that every run starts with (also used for branch previews). */
  readonly startSegmentId: string;
  /** Visual-only copies of the start module behind the start line (the start camera looks over them). */
  readonly leadInModules: number;
  /** Max attempts to find a playable obstacle layout before falling back to an empty row. */
  readonly maxLayoutAttempts: number;
  /** Probability that a decoration socket receives a prop. */
  readonly decorationFillChance: number;
  /** Probability of a collectible line per segment. */
  readonly collectibleLineChance: number;
  /** Probability that a bonus collectible replaces one bottle of a line. */
  readonly bonusCollectibleChance: number;
  /**
   * Which bonus it is (relative weights): toolbox = points, helmet = extra life (sheets SEG_20–23), bottle = no bonus
   * (keeps the draw count of the old crate share). Crates: turboCrates.
   */
  readonly bonusMix: Readonly<Partial<Record<CollectibleType, number>>>;
  readonly turboCrates: TurboCrateConfig;
  /** Extra height for collectibles placed over a jump obstacle (meters). */
  readonly collectibleJumpArcHeight: number;
  readonly junctions: JunctionConfig;
  readonly terrain: TerrainConfig;
  /** Containers (walkable tops reached by a double jump): free run-up and roof bottles. */
  readonly roofRoutes: RoofRouteConfig;
  readonly setPieces: SetPiecesConfig;
}

export const LEVEL_CONFIG: LevelConfig = {
  segmentsAhead: 8,
  segmentsBehind: 1,
  // One free 20 m start module (≈ 2.5 s at the start speed) – then the warm-up obstacles begin (user 2026-10-02).
  safeStartSegments: 1,
  startSegmentId: 'SEG_STRAIGHT_20M',
  leadInModules: 2,
  maxLayoutAttempts: 8,
  decorationFillChance: 0.65,
  collectibleLineChance: 0.75,
  bonusCollectibleChance: 0.08,
  // The crate's former 0.55 share keeps the bottle (crates only where hard to reach – turboCrates): toolbox and helmet
  // stay as frequent as before and the random stream of the track is unchanged.
  bonusMix: { [CollectibleType.BeerBottle]: 0.55, [CollectibleType.Toolbox]: 0.3, [CollectibleType.ExtraLife]: 0.15 },
  // Rare and exposed (user 2026-10-03: "exponiert und schwierig zum Erreichen … und auch nicht so viele"): the end of a
  // container-roof line (double jump onto the roof) or the top of a line from 3.6 m up (only the double-jump arcs at
  // 4.8–5 m, no longer the 3.2–3.4 m lines), from 800 m on – with the tiers' fewer lines ≈ 0.4 per km early, fewer later.
  // 2026-10-05: the harder mix brings more special modules with high lines – the chances lowered so the turbo stays rare
  turboCrates: { roofChance: 0.08, highLineChance: 0.1, minHeight: 3.6, fromDistance: 800 },
  // Fewer bottles late in the run: DifficultyTier.bottleShare (steps at 2000, 4000, 6000, 8000 m).
  collectibleJumpArcHeight: 1.6,
  junctions: {
    minSegmentsBetween: 4, // 4 → 5 (2026-10-02): regular junction gaps, median ≈ 220 m; back to 4 (2026-10-05 late): the longer drum and crane series (60 m modules) stretched the gaps – junctions carry the crossing posters
    branchPreviewSegments: 4,
    // The square begins half the crossing road (3.3 m) before the pivot; plus 70 ms of touch latency: 4.0 m at 10 m/s,
    // 4.56 m at top speed, 6.45 m in the turbo. Was 14 m: a press well before the junction already turned.
    decisionWindow: 3.3,
    decisionLatencySeconds: 0.07,
    approachWindow: 14,
    lateTurnGrace: 2.6,
    maxHeadingDegrees: 90,
    minReversalSeparation: 64,
    roadsideClearance: 9, // drops the first bay (z 0–10) on both sides, keeps the second (z 10–20)
  },
  terrain: {
    jumpGapSafety: 1,
    maxRampSlope: 0.35,
    connectorClearance: 2,
    spawnClearance: 1,
    respawnMargin: 2,
    respawnSearchDistance: 60,
    sampleStep: 0.05,
    rowMergeDistance: 0.75,
    runAcrossSafety: 0.4,
    // 2.59 m container roof: 0.35 s for the second jump at best; ≥ 0.2 s when the face is met 0.46–1.03 s after take-off
    doubleJumpStep: { minTriggerWindow: 0.2, minTakeOffWindow: 0.25 },
  },
  // user 2026-10-02: "zumindest häufig" a bottle line on the container roof
  roofRoutes: { bottleChance: 0.8, bottleSpacing: 2, edgeMargin: 1, arc: true },
  // Two highlights never back to back (user 2026-10-02: the dozer ≈ every 600 m besides the golden ramps): 100 m =
  // five road modules, ≥ 5.5 s at top speed, ≈ 9 s at the speed of the first marks – the Bau-Quiz after the golden
  // bottle is over and the runner back in rhythm before the next set piece starts.
  setPieces: { minGap: 100 },
};
